import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface VerifiedUser {
  id: string;
  username: string;
  photo_url: string;
  bio: string;
  is_verified: boolean;
}

const VerifiedJournalists: React.FC = () => {
  const { currentUser } = useAuth();
  const [verifiedUsers, setVerifiedUsers] = React.useState<VerifiedUser[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchVerifiedUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, photo_url, bio, is_verified')
          .eq('is_verified', true)
          .order('username');

        if (error) throw error;
        setVerifiedUsers(data || []);
      } catch (error) {
        console.error('Error fetching verified users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVerifiedUsers();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Verified Journalists</h1>
        <Shield className="h-8 w-8 text-purple-400" />
      </div>

      <div className="bg-[#272727] rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white mb-3">
              Get Verified as a Journalist
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Join our community of verified journalists to enhance your credibility and reach. 
              Our verification badge is granted to professional journalists, editors, and recognized 
              media professionals who meet our verification criteria. Verified journalists enjoy 
              increased visibility and trust from our community.
            </p>
          </div>
          {!currentUser?.is_verified && (
            <Link
              to="/home/verify"
              className="flex items-center px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors whitespace-nowrap group"
            >
              <span>Apply Now</span>
              <ArrowRight className="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {verifiedUsers.map((user) => (
          <Link
            key={user.id}
            to={`/home/profile/${user.username.toLowerCase()}`}
            className="bg-[#272727] rounded-xl p-4 hover:bg-[#404040] transition-colors group"
          >
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src={user.photo_url}
                  alt={user.username}
                  className="w-16 h-16 rounded-full object-cover border-2 border-purple-500"
                />
                <Shield className="absolute -bottom-1 -right-1 h-5 w-5 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-white group-hover:text-purple-400 transition-colors">
                  {user.username}
                </h3>
                {user.bio && (
                  <p className="text-gray-400 text-sm line-clamp-2">{user.bio}</p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {verifiedUsers.length === 0 && (
        <div className="text-center py-12">
          <Shield className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-300 mb-2">
            No Verified Journalists Yet
          </h3>
          <p className="text-gray-400">
            Be the first to get verified and join our trusted community.
          </p>
        </div>
      )}
    </div>
  );
};

export default VerifiedJournalists;